import { handleSetScenes } from '@/storage/modules/scenes/reducer'
import { SceneDataProps } from '@/types/sceneProps'
import { IoTrash } from 'react-icons/io5'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import colors from 'tailwindcss/colors'
import { FormScene } from '../FormScene'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { EmptyState } from '../EmptyState'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { SCENES_UPDATED_EVENT } from '@/services/scenesEvents'

export function Scenes() {
  const { openModal } = useModal()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const [scenesList, setScenesList] = useState<SceneDataProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const canLoadMoreRef = useRef(false)

  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchScenesPage = async (page: number) => {
    const response = await api.get<PaginatedResponseProps<SceneDataProps>>(
      '/scenes',
      {
        params: {
          page,
        },
      },
    )

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      scenes: response.data.data,
      pagination,
    }
  }

  const syncScenesStore = async () => {
    const responseScenes = await api
      .get('/scenes', {
        params: {
          page: 1,
        },
      })
      .then((res) => res.data.data as SceneDataProps[])

    dispatch(handleSetScenes({ scenes: responseScenes }))
  }

  const handleLoadFirstPage = async () => {
    try {
      setIsLoadingInitial(true)

      const response = await handleFetchScenesPage(1)
      setScenesList(response.scenes)
      setPaginationMeta(response.pagination)
      await syncScenesStore()
    } catch (error) {
      showToast('Error fetching scenes', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoadingInitial(false)
    }
  }

  const handleRemoveScene = async (sceneId: string) => {
    try {
      await api.delete(`/scenes/${sceneId}`)
      await syncScenesStore()
      setScenesList((prev) => prev.filter((scene) => scene.id !== sceneId))
      setPaginationMeta((prev) => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1),
      }))

      showToast('Scene removed successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (error) {
      showToast('Error removing scene', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    handleLoadFirstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    paginationMetaRef.current = paginationMeta
    canLoadMoreRef.current = canLoadMore
  }, [canLoadMore, paginationMeta])

  useEffect(() => {
    const loadMoreScenes = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchScenesPage(nextPage)
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        setScenesList((prev) => [...prev, ...response.scenes])
        setPaginationMeta(response.pagination)
      } catch (error) {
        showToast('Error fetching more scenes', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (!canLoadMoreRef.current || isLoadingMoreRef.current) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreScenes()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [showToast])

  useEffect(() => {
    const handleScenesUpdated = () => {
      handleLoadFirstPage()
    }

    window.addEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)

    return () => {
      window.removeEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {scenesList.length} of {paginationMeta.totalItems} scenes
        </p>
      </div>

      {scenesList.length === 0 && !isLoadingInitial && (
        <EmptyState
          title="No scenes yet"
          description="Create the first scene to organize musical genres."
        />
      )}

      {scenesList.map((scene) => (
        <div key={scene.id} className="relative mt-8">
          <button
            title="Remove"
            className="absolute -right-1.5 -top-1.5 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-red-600 bg-red-600 shadow-sm transition hover:bg-red-700"
            onClick={() => {
              openModal({
                textConfirm: 'Delete',
                description: 'Do you really want to delete the scene?',
                title: 'Attention',
                confirm() {
                  handleRemoveScene(scene.id)
                },
              })
            }}
          >
            <IoTrash size={14} color={colors.white} />
          </button>

          <button
            className="bg-white rounded-2xl p-7 top-5 flex gap-12 justify-between items-center border hover:border-gray-300 w-full"
            onClick={() => {
              openModal({
                children: <FormScene isExist={scene} />,
              })
            }}
          >
            <p className="font-semibold text-gray-500">{scene.title}</p>
            <p className="text-sm text-gray-400">{scene.slug}</p>
          </button>
        </div>
      ))}

      {(isLoadingInitial || isLoadingMore) && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>
            {isLoadingInitial ? 'Loading scenes...' : 'Loading more scenes...'}
          </p>
        </div>
      )}
    </div>
  )
}
